import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable react/no-danger */
/* eslint-disable react/destructuring-assignment */
import React, { useEffect } from 'react';
import { Box, Skeleton } from '@mui/material';
import Carousel from 'react-multi-carousel';
// import 'react-multi-carousel/lib/styles.css';
import { useStore, } from '@mcn-platform/models';
import { observer } from 'mobx-react-lite';
import MediaCard from '../item/card/MediaCard';
import getData from '../item/getData';
import PlaceHolder from '../common/PlaceHolder';
import WysiwygTitle from '../item/WysiwygTitle';
import { responsive } from '../item/ItemCarousel';
import { getMeetingSearchFilter, } from './meetingHelpers';
const CarouselSkeleton = () => (_jsx(Box, { sx: {
        display: 'flex',
        gap: 2,
        overflow: 'hidden',
        '& > *': {
            flexShrink: 0,
        },
    }, children: [...Array(4).keys()].map((i) => (_jsxs(Box, { sx: {
            minWidth: { xs: '100%', sm: '280px' },
            maxWidth: { xs: '100%', sm: '280px' },
            margin: '4px 8px',
        }, children: [_jsx(Skeleton, { variant: "rectangular", width: "100%", height: 200, sx: { mb: 1 } }), _jsx(Skeleton, { variant: "text", width: "80%", height: 24, sx: { mb: 0.5 } }), _jsx(Skeleton, { variant: "text", width: "60%", height: 20, sx: { mb: 1 } }), _jsx(Skeleton, { variant: "text", width: "100%", height: 16 }), _jsx(Skeleton, { variant: "text", width: "90%", height: 16 })] }, i))) }));
const MeetingCarousel = ({ statusOption = 'upcoming', accessOption = 'hasAccess', membershipID, content, viewAction, registerAction, editData, showTitle = true, maxItemCount, padding, backgroundColor, configId, limitToPageWidth, disabled = false, }) => {
    const [meetings, setMeetings] = React.useState(undefined);
    const store = useStore();
    const { currentCustomer, currentAccess, getCachedList, fetchCachedList, } = store;
    useEffect(() => {
        const loadData = async () => {
            console.log('loading data', {
                statusOption,
                accessOption,
                currentAccess,
                membershipID,
            });
            const searchFilter = getMeetingSearchFilter(statusOption, currentCustomer, currentAccess, accessOption);
            const newList = getCachedList('meeting', searchFilter);
            newList.setRequestedCount(1000);
            await fetchCachedList(newList);
            setMeetings(newList.items);
        };
        loadData();
    }, [
        accessOption,
        currentCustomer,
        getCachedList,
        fetchCachedList,
        membershipID,
        statusOption,
        currentAccess,
    ]);
    // empty if no category has been selected and no items have been selected
    // and no title has been set.
    const isEmpty = !meetings || meetings.length === 0;
    if (isEmpty && editData && editData.activeConfigId !== configId) {
        return (_jsx(PlaceHolder, { placeHolderName: "Configure Meeting Carousel" }));
    }
    const isServer = typeof window === 'undefined';
    return (_jsxs(Box, { sx: {
            backgroundColor: { backgroundColor },
            p: (`${padding}px`),
            ...(limitToPageWidth && {
                maxWidth: 1200,
                marginX: 'auto',
                width: '100%',
            }),
        }, children: [_jsx(WysiwygTitle, { showTitle: showTitle, editData: editData, content: content, configId: configId, disabled: disabled }), meetings?.length === 0 && editData && (_jsx(PlaceHolder, { placeHolderName: "Carousel" })), (meetings && meetings.length > 0) && (isServer ? (_jsx(CarouselSkeleton, {})) : (_jsx(Carousel, { responsive: responsive, ssr: false, children: meetings.slice(0, maxItemCount).map((meeting) => (_jsx(Box, { sx: {
                        margin: '4px 8px',
                    }, children: _jsx(MediaCard, { horizontal: false, size: "large", showPrice: false, item: meeting, viewAction: viewAction, registerAction: registerAction }) }, meeting.key))) })))] }));
};
MeetingCarousel.defaultProps = {
    content: {
        slate: undefined,
        html: '<div>Title</div>',
    },
    statusOption: 'upcoming',
    accessOption: 'hasAccess',
    membershipID: undefined,
    showTitle: true,
    maxItemCount: 20,
    viewAction: undefined,
    registerAction: undefined,
    padding: 0,
    disabled: false,
};
// already does what we need
MeetingCarousel.getData = getData;
MeetingCarousel.meta = {
    group: 'display',
    name: 'Meeting Carousel',
    description: 'Displays meetings from a category or group in a carousel.',
    props: [
        {
            key: 'statusOption',
            name: 'Status Filter',
            type: 'array',
            items: [
                { value: 'upcoming', label: 'Upcoming' },
                { value: 'past', label: 'Past' },
                { value: 'both', label: 'Both' },
            ],
            description: 'Controls whether to show upcoming, past, or both meetings.',
        },
        {
            key: 'accessOption',
            name: 'Access Filter',
            type: 'array',
            items: [
                { value: 'registered', label: 'Registered' },
                { value: 'invited', label: 'Invited' },
                { value: 'registeredOrInvited', label: 'Registered or Invited' },
                { value: 'hasAccess', label: 'Has Access' },
                // { value: 'membership', label: 'Membership' },
            ],
            description: 'Controls what meetings to display based on registration or access.',
        },
        // {
        //   key: 'membershipKey',
        //   name: 'Membership',
        //   type: 'membership',
        //   description: 'Show meetings based on this membership.',
        // },
        {
            key: 'showTitle',
            name: 'Show Title',
            type: 'boolean',
            description: 'Show the title above the carousel',
        },
        {
            key: 'content',
            name: 'Title',
            type: 'richtext',
            description: 'Title of carousel',
            inPlaceEdit: true,
        },
        {
            key: 'maxItemCount',
            name: 'Maximum Items',
            type: 'number',
            description: 'Maximum number of items to appear in carousel.',
        },
        {
            key: 'limitToPageWidth',
            name: 'Limit Width',
            type: 'boolean',
            description: 'Limit the width of the stack to the maximum page width',
            group: 'style',
        },
        {
            key: 'layout',
            name: 'Layout',
            type: 'layout',
            description: 'Layout properties such as spacing and background color.',
            group: 'style',
        },
    ],
    defaultConfig: {
        type: 'component',
        component: 'MeetingCarousel',
        props: {
            statusOption: 'upcoming',
            accessOption: 'hasAccess',
            content: {
                slate: undefined,
                html: '<div>&nbsp;</div>',
            },
            showTitle: true,
            maxItemCount: 20,
            category: '@categoryId',
            limitToPageWidth: true,
        },
    },
};
MeetingCarousel.IpeWrapper = WysiwygTitle.IpeWrapper;
MeetingCarousel.ipeControls = WysiwygTitle.ipeControls;
export default observer(MeetingCarousel);
//# sourceMappingURL=MeetingCarousel.js.map